// Central product data. Gallery images are auto-imported per folder with
// require.context so new photos just need to be dropped into src/assets/*
// without touching any component code.
//
// Each category's `images` list is pinned to an explicit, hand-curated
// `order` (matching the original site's manual ordering) rather than a
// plain filename sort, since the photos aren't numbered in display order.

import mainGrillImage from "../assets/psistaria/psistaria main.webp";
import churchCardImage from "../assets/churches/6l.webp";
import stoneCardImage from "../assets/stones/34.webp";
import sinkCardImage from "../assets/sink/outdoor2.webp";
import vaseCardImage from "../assets/vase/44.webp";
import sculptureCardImage from "../assets/sculptures/56.webp";

function numberIn(path) {
  const match = path.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

// options.sortKey: custom comparable value derived from the require.context key.
// options.order: manual sequence — list filenames (e.g. "5.webp") in the exact
// order you want them to appear. Files left out keep their normal order and
// are appended after the ones you listed.
// options.strict: when true, ONLY the files listed in `order` are included
// (anything else in the folder is left out of the gallery entirely).
function loadGallery(context, options = {}) {
  const { sortKey, order, strict } = options;
  const entries = context.keys().map((key) => ({ key, src: context(key) }));

  if (order && order.length) {
    const rank = new Map(order.map((name, i) => [name, i]));
    const source = strict ? entries.filter((e) => rank.has(e.key.replace(/^\.\//, ""))) : entries;

    return source.sort((a, b) => {
      const nameA = a.key.replace(/^\.\//, "");
      const nameB = b.key.replace(/^\.\//, "");
      const rankA = rank.has(nameA) ? rank.get(nameA) : Infinity;
      const rankB = rank.has(nameB) ? rank.get(nameB) : Infinity;
      return rankA !== rankB ? rankA - rankB : numberIn(a.key) - numberIn(b.key);
    });
  }

  return entries.sort((a, b) =>
    sortKey ? sortKey(a.key) - sortKey(b.key) : numberIn(a.key) - numberIn(b.key)
  );
}

/* ---------------- Grills, Rotisseries & Pizza Ovens ---------------- */
const grillCopy = {
  1: { name: "Luxury Outdoor Grill", description: "Perfect for outdoor events" },
  2: { name: "Stainless Steel Grill", description: "Durable, easy to clean" },
  3: { name: "Classic Grill" },
  4: { name: "Premium Grill", description: "Premium cooking experience" },
  5: { name: "Classic Grill" },
  6: { name: "Outdoor Grill", description: "Garden cooking experience" },
  7: { name: "Rotisseries & Pizza Ovens", description: "Garden cooking experience" },
};
const grillsImages = loadGallery(require.context("../assets/psistaria", false, /^\.\/\d+\.webp$/i), {
  order: ["1.webp", "2.webp", "3.webp", "4.webp", "5.webp", "6.webp", "7.webp"],
}).map(({ key, src }) => {
  const n = numberIn(key);
  return { src, alt: grillCopy[n]?.name || "Outdoor grill", ...grillCopy[n] };
});

/* ---------------- Memorial Church Shrines ---------------- */
const sizeName = { l: "Large", m: "Medium", s: "Small" };
const churchOrder = [
  "1l.webp",
  "2l.webp",
  "3l.webp",
  "1m.webp",
  "2m.webp",
  "3m.webp",
  "3s.webp",
  "2s.webp",
  "1s.webp",
];
const churchImages = loadGallery(require.context("../assets/churches", false, /\.webp$/i), {
  order: churchOrder,
  strict: true,
}).map(({ key, src }) => {
  const m = key.match(/(\d+)([lms])/i);
  const size = m ? sizeName[m[2].toLowerCase()] : "";
  return { src, alt: `Memorial church shrine, ${size} size`, name: `Memorial Church Shrine (${size} size)` };
});

/* ---------------- Decorative Stones & Stylish Tiles ---------------- */
const stonesNames = {
  "4.webp": "Decorative Stones",
  "1.webp": "Decorative Stones",
  "3.webp": "Decorative Stones",
  "5.webp": "Decorative Stones",
  "32.webp": "Decorative Stones",
  "6.webp": "Decorative Stones",
  "9.webp": "Decorative Stones",
  "8.webp": "Decorative Stones",
  "7.webp": "Decorative Stones",
  "26.webp": "Decorative Stones",
  "25.webp": "Decorative Wall Tiles",
  "24.webp": "Decorative Stones",
  "13.webp": "Decorative Wall Tiles",
  "14.webp": "Decorative Wall Tiles",
  "15.webp": "Decorative Tiles",
  "27.webp": "Decorative Tiles",
  "29.webp": "Decorative Wall Tiles",
  "18.webp": "Decorative Wall Tiles",
  "20.webp": "Decorative Wall Tiles",
  "22.webp": "Decorative Stones",
  "21.webp": "Decorative Stones",
  "11.webp": "Decorative Stones",
  "17.webp": "Decorative Stones",
  "30.webp": "Decorative Stones",
  "19.webp": "Decorative Stones",
  "2.webp": "Decorative Stone Borders",
  "12.webp": "Decorative Stones",
  "28.webp": "Decorative Stones",
};
const stonesImages = loadGallery(require.context("../assets/stones", false, /\.webp$/i), {
  order: Object.keys(stonesNames),
  strict: true,
}).map(({ key, src }) => {
  const name = stonesNames[key.replace(/^\.\//, "")];
  return { src, alt: name, name };
});

/* ---------------- Sculptures ---------------- */
const sculpturesNames = {
  "22.webp": "Sculptures",
  "19.webp": "Sculptures",
  "14.webp": "Sculptures",
  "13.webp": "Sculptures",
  "5.webp": "Sculptures",
  "18.webp": "Sculptures",
  "4.webp": "Sculptures",
  "20.webp": "Sculptures",
  "2.webp": "Sculptures",
  "17.webp": "Sculptures",
  "8.webp": "Sculptures",
  "15.webp": "Sculptures",
  "3.webp": "Sculptures",
  "21.webp": "Sculptures",
  "7.webp": "Sculptures",
  "11.webp": "Sculptures",
  "6.webp": "Sculptures",
  "12.webp": "Sculptures",
  "9.webp": "Sculptures",
  "1.webp": "Sculptures",
  "33.webp": "Sculptures",
  "48.webp": "Garden Stone Table",
  "51.webp": "Garden Stone Table",
  "34.webp": "Sculptures",
  "50.webp": "Garden Stone Table",
  "49.webp": "Garden Stone Table",
  "37.webp": "Sculptures",
  "38.webp": "Sculptures",
  "39.webp": "Sculptures",
  "36.webp": "Sculptures",
  "27.webp": "Sculptures (Small Size)",
  "24.webp": "Sculptures (Small Size)",
  "46.webp": "Sculptures (Small Size)",
  "25.webp": "Sculptures (Small Size)",
  "26.webp": "Sculptures (Small Size)",
  "28.webp": "Sculptures (Small Size)",
  "32.webp": "Sculptures (Small Size)",
  "53.webp": "Sculptures (Small Size)",
  "43.webp": "Sculptures (Small Size)",
  "40.webp": "Sculptures (Small Size)",
  "29.webp": "Sculptures (Small Size)",
  "35.webp": "Sculptures (Small Size)",
  "30.webp": "Sculptures (Small Size)",
  "54.webp": "Sculptures (Small Size)",
  "41.webp": "Sculptures (Small Size)",
  "47.webp": "Sculptures (Small Size)",
  "45.webp": "Sculptures (Small Size)",
  "44.webp": "Sculptures",
  "55.webp": "Sculptures",
  "42.webp": "Sculptures",
  "52.webp": "Sculptures",
};
const sculpturesImages = loadGallery(require.context("../assets/sculptures", false, /\.webp$/i), {
  order: Object.keys(sculpturesNames),
  strict: true,
}).map(({ key, src }) => {
  const name = sculpturesNames[key.replace(/^\.\//, "")];
  return { src, alt: name, name };
});

/* ---------------- Outdoor Sinks & Garden Fountains ---------------- */
const sinkNames = {
  "outdoor.webp": "Stone Outdoor Fountains (Standard Size)",
  "outdoor1.webp": "Stone Outdoor Fountains (Standard Size)",
  "fountain.webp": "Stone Outdoor Fountains (Standard Size)",
  "outdoor2.webp": "Stone Outdoor Fountains (Standard Size)",
  "outdoor3.webp": "Stone Outdoor Fountains (Standard Size)",
  "fountain3.webp": "Stone Outdoor Fountains (Standard Size)",
  "fountain1.webp": "Stone Outdoor Fountains (Standard Size)",
  "fountain4.webp": "Stone Outdoor Fountains (Standard Size)",
  "fountain6.webp": "Stone Outdoor Fountains (Standard Size)",
  "fountain10.webp": "Stone Outdoor Fountains (Standard Size)",
  "fountain2.webp": "Stone Outdoor Fountains (Standard Size)",
  "fountain8.webp": "Stone Outdoor Fountains (Standard Size)",
  "fountain7.webp": "Stone Outdoor Fountains (Standard Size)",
  "fountain9.webp": "Stone Outdoor Fountains (Standard Size)",
  "fountain11.webp": "Stone Outdoor Fountains (Standard Size)",
  "1.webp": "Stone Outdoor Sinks (Small Size)",
  "2.webp": "Stone Outdoor Sinks (Small Size)",
  "fountain5.webp": "Stone Outdoor Fountains (Standard Size)",
  "3.webp": "Stone Outdoor Sinks (Small Size)",
  "4.webp": "Stone Outdoor Sinks (Small Size)",
};
const sinkImages = loadGallery(require.context("../assets/sink", false, /\.webp$/i), {
  order: Object.keys(sinkNames),
  strict: true,
}).map(({ key, src }) => {
  const name = sinkNames[key.replace(/^\.\//, "")];
  return { src, alt: name, name };
});

/* ---------------- Patio & Outdoor Vases ---------------- */
const vaseNames = {
  "34.webp": "Vases (Small Size)",
  "35.webp": "Vases (Small Size)",
  "30.webp": "Vases (Small Size)",
  "32.webp": "Vases (Small Size)",
  "29.webp": "Vases (Small Size)",
  "37.webp": "Vases (Medium Size)",
  "36.webp": "Vases (Medium Size)",
  "39.webp": "Vases (Medium Size)",
  "9.webp": "Vases (Medium Size)",
  "25.webp": "Vases (Medium Size)",
  "38.webp": "Vases (Medium Size)",
  "40.webp": "Vases (Medium Size)",
  "26.webp": "Vases (Medium Size)",
  "24.webp": "Vases (Medium Size)",
  "22.webp": "Vases (Medium Size)",
  "27.webp": "Vases (Medium Size)",
  "28.webp": "Vases (Medium Size)",
  "18.webp": "Vases (Medium Size)",
  "20.webp": "Vases (Medium Size)",
  "21.webp": "Vases (Medium Size)",
  "17.webp": "Vases (Medium Size)",
  "19.webp": "Vases (Medium Size)",
  "3.webp": "Vases (Large Size)",
  "42.webp": "Vases (Large Size)",
  "4.webp": "Vases (Large Size)",
  "6.webp": "Vases (Large Size)",
  "11.webp": "Vases (Large Size)",
  "13.webp": "Vases (Large Size)",
  "41.webp": "Vases (Large Size)",
  "1.webp": "Vases (Large Size)",
  "15.webp": "Vases (Large Size)",
  "7.webp": "Vases (Large Size)",
  "8.webp": "Vases (Large Size)",
  "43.webp": "Vases (Large Size)",
  "2.webp": "Vases (Large Size)",
  "14.webp": "Vases (Large Size)",
  "5.webp": "Vases (Large Size)",
};
const vaseImages = loadGallery(require.context("../assets/vase", false, /\.webp$/i), {
  order: Object.keys(vaseNames),
  strict: true,
}).map(({ key, src }) => {
  const name = vaseNames[key.replace(/^\.\//, "")];
  return { src, alt: name, name };
});

export const categories = [
  {
    slug: "grills",
    navTitle: "Grills, Rotisseries & Pizza Ovens",
    title: "Grills, Rotisseries & Pizza Ovens",
    orderLabel: "Grills, Rotisseries & Pizza Ovens",
    cardImage: mainGrillImage,
    shortInfo: [
      "Premium-grade stainless steel built for everyday use",
      "Perfect for gardens, patios, and outdoor entertaining",
      "Available in a range of models to suit every outdoor space",
    ],
    description: [
      "Our grills are designed with a clear focus on simplicity, durability, and everyday usability. Each unit is carefully proportioned to fit naturally into outdoor spaces.",
      "We use heat-resistant materials and stone finishes built for long-term outdoor performance.",
      "From selection to installation, everything is handled and delivered ready to use.",
    ],
    features: [
      "Sizes: Multiple configurations",
      "Finish: Neutral, modern tones",
      "Stone: Natural / engineered",
      "Sink: Optional integration",
      "Durability: Weather + heat resistant",
      "Delivery & installation: Included",
    ],
    images: grillsImages,
  },
  {
    slug: "church",
    navTitle: "Memorial Church Shrines",
    title: "Memorial Church Shrines",
    orderLabel: "Memorial Church Shrines",
    cardImage: churchCardImage,
    shortInfo: [
      "Designed to enhance gardens, memorials, and sacred spaces",
      "Expertly handcrafted with timeless Orthodox elegance",
      "Offered in Small, Medium, and Large sizes for every setting",
    ],
    description: [
      "Discover our handcrafted collection of Orthodox memorial shrines, thoughtfully created to honour tradition while adding timeless beauty to gardens, memorial spaces, churches, and private properties.",
      "Available in Small, Medium, and Large sizes, each shrine is carefully finished by hand and offered in a variety of elegant colour combinations, including classic white & blue, white & red, natural stone, grey, beige, green, and custom finishes upon request.",
      "Every piece is crafted using durable, weather-resistant materials, ensuring exceptional quality, long-lasting performance, and a beautiful appearance for years to come.",
      "We warmly welcome you to visit our showroom, where you can experience the craftsmanship, colours, and available sizes in person before making your choice.",
    ],
    images: churchImages,
  },
  {
    slug: "stones",
    navTitle: "Decorative & Outdoor Stones",
    title: "Decorative Stones & Stylish Tiles",
    orderLabel: "Decorative Stones & Stylish Tiles",
    cardImage: stoneCardImage,
    shortInfo: [
      "Available in a variety of sizes, colours, and textures",
      "Suitable for both residential and commercial landscaping",
      "Ideal for pathways, borders, and decorative outdoor spaces",
    ],
    description: [
      "Discover our handcrafted collection of decorative stones and stylish tiles, designed to bring elegance, durability, and timeless character to both indoor and outdoor spaces. Perfect for homes, gardens, patios, walkways, and commercial projects, our range combines quality craftsmanship with modern design.",
      "Available in a variety of sizes, textures, colours, and finishes, our collection includes natural stone, contemporary porcelain tiles, decorative paving, mosaic designs, and custom options to suit every style and architectural vision.",
      "Manufactured using high-quality, weather-resistant materials, our decorative stones and tiles are built to withstand everyday use while maintaining their beauty, strength, and long-lasting performance.",
      "Visit our showroom to explore our complete range, compare colours and finishes, and receive expert guidance to help you select the perfect decorative stones and tiles for your next project.",
    ],
    images: stonesImages,
  },
  {
    slug: "sink",
    navTitle: "Outdoor Sinks & Fountains",
    title: "Outdoor Sinks & Garden Fountains",
    orderLabel: "Outdoor Sinks & Garden Fountains",
    cardImage: sinkCardImage,
    shortInfo: [
      "Expertly handcrafted with a focus on timeless elegance",
      "A curated selection of sizes and finishes for any outdoor space",
      "Engineered from premium, weather-resistant materials",
    ],
    description: [
      "Discover our handcrafted collection of premium outdoor sinks and decorative garden fountains, designed to combine timeless craftsmanship with everyday practicality for gardens, patios, terraces, and outdoor living spaces.",
      "Available in a wide range of sizes, colours, and finishes, every piece is carefully handcrafted using durable, weather-resistant materials. Choose from natural stone, marble-effect, brick, mosaic, terrazzo, and custom colour combinations to perfectly complement your outdoor space.",
      "Built for year-round outdoor use, our sinks and fountains are designed to withstand the elements while maintaining their beauty and functionality for many years.",
      "Visit our showroom to explore our complete collection, compare finishes, and receive expert advice to help you choose the perfect outdoor sink or fountain for your home or project.",
    ],
    images: sinkImages,
  },
  {
    slug: "vase",
    navTitle: "Patio & Outdoor Vases",
    title: "Patio & Outdoor Vases",
    orderLabel: "Patio & Outdoor Vases",
    cardImage: vaseCardImage,
    shortInfo: [
      "Elegant vases designed to enhance any garden or terrace",
      "A stylish and modern touch for outdoor spaces",
      "Durable, rust-resistant materials for long-lasting use",
    ],
    description: [
      "Bring timeless elegance to your home or outdoor space with our carefully curated collection of premium ceramic vases. Beautifully crafted and available in small, medium, and large sizes, each piece is designed to complement a wide range of interior and exterior settings with effortless sophistication.",
      "Whether you're creating a striking entrance, enhancing a patio, styling a living space, or adding character to a landscaped garden, our ceramic vases are available in an elegant selection of colours and finishes to suit both contemporary and classic designs.",
      "Combining exceptional craftsmanship with lasting quality, our ceramic vases are suitable for both indoor and outdoor use. Their durable construction ensures they retain their beauty while providing a stylish home for your favourite plants, flowers, or decorative arrangements.",
      "Visit our showroom to discover the full collection, explore our range of colours and sizes, and let our experienced team help you choose the perfect ceramic vases to elevate your space.",
    ],
    images: vaseImages,
  },
  {
    slug: "sculptures",
    navTitle: "Sculptures",
    title: "Sculptures",
    orderLabel: "Sculptures",
    cardImage: sculptureCardImage,
    shortInfo: [
      "Unique handcrafted sculptures with character and artistry",
      "Careful attention to detail for a one-of-a-kind finish",
      "A bold and elegant statement for any home or garden",
    ],
    description: [
      "Discover our handcrafted collection of sculptures, designed to bring elegance, personality, and artistic expression to any indoor or outdoor space. Each piece is carefully crafted to enhance modern and classic settings with timeless beauty.",
      "Available in a variety of sizes, shapes, colours, and premium finishes, our collection includes contemporary designs, abstract forms, and custom-made sculptures to match any style or creative vision.",
      "Made using high-quality materials and perfected with detailed craftsmanship, our sculptures offer exceptional durability while maintaining flawless finishes and rich, long-lasting colours.",
      "Visit our showroom to explore the full range, compare finishes and designs, and receive expert guidance to help you choose the perfect handcrafted sculpture for your space.",
    ],
    images: sculpturesImages,
  },
];

export function getCategoryBySlug(slug) {
  return categories.find((category) => category.slug === slug);
}
